/**
 * THE Supabase client. One project, one client, two apps.
 *
 * WHY THIS IS A PACKAGE AND NOT A FILE IN EACH APP: `apps/legacy` and
 * `apps/student` are separate bundles served from the same origin, and both
 * talk to the same project (`fryfcvxbdjikxsvdpnks`). Two client
 * implementations would mean two GoTrue instances racing over the same
 * `localStorage` key — and, worse, two places to change a config that must
 * never disagree. Duplicating the API client is forbidden by design.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT DO: touch the kids `registry`. The old
 * `core/services/supabase.ts` imported `./registry`, which declares ~50 typed
 * slots for admin, CRM and finance services — importing it from here would
 * make the student app depend on the entire legacy type graph. The registry
 * wiring stays in `apps/legacy/core/services/supabase.ts`, layered on top of
 * this module; it registers THIS singleton, so there is still only one client.
 *
 * `storageKey` is load-bearing: same key + same origin is what lets a student
 * sign in on `/` (kids) and arrive at `/app/` (student) already authenticated.
 * Do not change it in one app without the other.
 */
import { createClient, processLock, type SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_STORAGE_KEY } from './storage-key';

export { SUPABASE_STORAGE_KEY } from './storage-key';

interface DatabaseModuleType {
    client: SupabaseClient | null;
    _initPromise: Promise<SupabaseClient | null> | null;
    init(): Promise<SupabaseClient | null>;
}

interface SupabaseConfig {
    SUPABASE_URL: string;
    SUPABASE_ANON_KEY: string;
}

declare global {
    interface Window {
        supabaseClient: SupabaseClient | null;
    }
}

const DatabaseModule: DatabaseModuleType = {
    client: null,
    _initPromise: null as Promise<SupabaseClient | null> | null,

    async init(): Promise<SupabaseClient | null> {
        if (this.client) return this.client;
        if (this._initPromise) return this._initPromise;

        /* SYNCHRONOUS BODY, PROMISE RESULT. `createClient` does not return a
           promise, so there is nothing here to await — this used to be an
           `async` IIFE, which read as if there were. The body ran to completion
           synchronously either way, so the timing is identical; only the
           misleading `async` is gone. `init()` stays async because every caller
           awaits it and because `_initPromise` is what de-duplicates concurrent
           boots. Extracted verbatim from the kids `core/services/supabase.ts`
           otherwise: a repo split is not the place to change auth boot. */
        const create = (): SupabaseClient | null => {
            try {
                // ── SSOT: both vars come from ONE place (.env.example → .env → Netlify env) ──
                const config: SupabaseConfig = {
                    SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL as string,
                    SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY as string
                };

                if (!config.SUPABASE_URL || !config.SUPABASE_ANON_KEY) {
                    const missing = [
                        !config.SUPABASE_URL && 'VITE_SUPABASE_URL',
                        !config.SUPABASE_ANON_KEY && 'VITE_SUPABASE_ANON_KEY',
                    ].filter(Boolean).join(', ');
                    console.error(
                        `❌ Database: Missing environment variables: ${missing}.\n` +
                        'Fix: Add them to Netlify → Site Settings → Environment Variables.\n' +
                        'Reference: .env.example at the repo root (all required vars listed there).'
                    );
                    return null;
                }

                // Global singleton first — prevents multiple GoTrue clients when both
                // this package and a legacy path try to initialise.
                if (window.supabaseClient) {
                    this.client = window.supabaseClient;
                    return this.client;
                }

                this.client = createClient(
                    config.SUPABASE_URL,
                    config.SUPABASE_ANON_KEY,
                    {
                        auth: {
                            persistSession: true,
                            autoRefreshToken: true,
                            detectSessionInUrl: false,
                            flowType: 'pkce',
                            storageKey: SUPABASE_STORAGE_KEY,
                            /* ONE LOCK PER TAB, not one per origin. The default Navigator
                               Lock is shared by every tab of app.theidiomaclub.com, so a
                               background tab Chrome throttles can hold it past the 10 s
                               acquire timeout — the next tab's sign-in then fails with
                               "No pudimos verificar tu cuenta" and F5 gets in (2026-10-12,
                               reproduced on /teacher/). Tabs still converge through the
                               storage event, and a near-simultaneous refresh from two tabs
                               falls inside GoTrue's refresh-token reuse interval. */
                            lock: processLock,
                            debug: false,
                        },
                        global: {
                            headers: {},
                        },
                    }
                );

                window.supabaseClient = this.client;
                return this.client;
            } catch (err) {
                console.error('❌ Database: Initialization failed:', err);
            } finally {
                this._initPromise = null;
            }
            return null;
        };

        this._initPromise = Promise.resolve(create());
        return this._initPromise;
    }
};

/** Synchronous accessor — use only after auth boot. */
export function getSupabaseClient(): SupabaseClient | null {
    return DatabaseModule.client || window.supabaseClient || null;
}

/**
 * Async accessor — safe for routes that render before the singleton exists
 * (placement test, enrollment landing, and the student app's own boot).
 */
export async function waitForClient(): Promise<SupabaseClient | null> {
    const existing = getSupabaseClient();
    if (existing) return existing;
    return DatabaseModule.init();
}

/** Same name as the student's lite entry (`lite.ts`); here the client is already complete. */
export async function loadSupabaseExtras(): Promise<SupabaseClient> {
    const client = await waitForClient();
    if (!client) throw new Error('[supabase] no client: check VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY');
    return client;
}

export { DatabaseModule };
export type { DatabaseModuleType, SupabaseClient };
export default DatabaseModule;
